import { JsonldBreadcrumb, JsonldProduct, TProductData } from '@dengigroup/seo-jsonld-markup';
import React, { FC } from 'react';

import { BlockBuyoutInfo, CalculatorContext, ProductCardCalc } from '@/components/modules/caclulator';
import { useCalculationInitStateContext } from '@/components/modules/caclulator/hooks';
import { TVariantsCreditProducts } from '@/components/modules/caclulator/types';
import { ArentaPriceInfo } from '@/components/modules/product-page/ui/arenta-price-info';
import { LandingControlButtons } from '@/components/modules/product-page/ui/landing-control-buttons';
import { useAppSelector } from '@/store/hooks';

import { Footer } from '@/widgets/footer';
import { Header } from '@/widgets/header';

import { HowToUse } from '@/entities/info';
import { HeadMeta, TLanding } from '@/entities/meta-seo';
import { setCoordinates } from '@/entities/office';
import { TFullRentProduct, ProductTitle, ProductTabs } from '@/entities/product';

import { BASE_URL } from '@/shared/constants/paths';
import { APP_BASE_URL } from '@/shared/constants/settings';
import { useTranslate } from '@/shared/hooks/use-translate';
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import { Layout } from '@/shared/ui/layout';

import { ProductImages } from '../../product-images';
import { StickyBottomNavigation } from '../../sticky-bottom-navigation';

import { StandardControlButtons } from './standard-control-buttons';
import * as Styled from './styled';

type TProps = {
    title: string;
    description: string;
    product: TFullRentProduct;
    variantsCreditProducts: TVariantsCreditProducts;
    landInfo?: TLanding;
};

export const ProductPage: FC<TProps> = ({ description, title, product, variantsCreditProducts, landInfo }) => {
    const { t } = useTranslate();

    const cityUser = useAppSelector((state) => state.user.cityUser);

    const contextState = useCalculationInitStateContext();

    const seoLinkedProduct: TProductData = {
        price: product.prices.priceForThirtyDays?.toString() ?? '0',
        image: product.photos?.find((image) => image.isMain === true)?.image ?? '',
        name: product.name,
        description: product.description,
        priceCurrency: 'KZT',
        inStock: product.status === 'active',
    };

    const breadcrumbs = [
        {
            name: t('Главная'),
            url: BASE_URL,
        },
        {
            name: t('Каталог аренды'),
            url: `/${cityUser.slug}`,
        },
        {
            name: t(product.category.name),
            url: `/${cityUser.slug}/${product.category.slug}`,
        },
        {
            name: t(product.name),
            url: `/product/${product.id}`,
        },
    ];

    return (
        <CalculatorContext.Provider value={contextState}>
            <Layout header={<Header />} footer={<Footer />}>
                <HeadMeta title={title} description={description} />
                <JsonldBreadcrumb data={breadcrumbs} domain={APP_BASE_URL as string} />
                <JsonldProduct data={seoLinkedProduct} />
                <Layout.Content>
                    {!landInfo && <Breadcrumbs breadcrumbs={breadcrumbs} />}
                    <ProductTitle
                        name={landInfo?.productName ?? product?.name}
                        location={product?.storageAddress}
                        city={product.storageCity}
                        coords={product.storageSalePointsCoordinates}
                        setCoordinates={setCoordinates}
                    />

                    <Styled.Wrapper>
                        <Styled.ProductContainer $isLang={Boolean(landInfo)}>
                            <ProductImages images={product.photos} title={product.name} />
                            <div className="right-column">
                                <div className="column__calculation">
                                    <div className="column__block-calc">
                                        <ProductCardCalc
                                            className="block-calc__component"
                                            product={product}
                                            variantsCreditProducts={variantsCreditProducts}
                                        />
                                    </div>
                                    <div className="column__block-info-calc">
                                        <ArentaPriceInfo product={product} />
                                        {product.is_buyout && (
                                            <div className="block-info-calc__buyout">
                                                <BlockBuyoutInfo />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="column__action">
                                    {landInfo ? (
                                        <LandingControlButtons product={product} />
                                    ) : (
                                        <StandardControlButtons product={product} />
                                    )}
                                </div>
                            </div>
                        </Styled.ProductContainer>
                        {/* <ProductAddons images={productAddons} /> */}
                    </Styled.Wrapper>

                    <Styled.TabsWrapper>
                        <ProductTabs description={product.description} options={product.options} />
                        <HowToUse />
                    </Styled.TabsWrapper>
                </Layout.Content>
            </Layout>
            <StickyBottomNavigation product={product} isLand={Boolean(landInfo)} />
        </CalculatorContext.Provider>
    );
};
